import { EyeOutlined } from '@ant-design/icons';
import { App, Button, Col, DatePicker, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import {
  PROJECT_DAILY_REPORT_STATUS_LABELS,
  projectDailyReportStatusTagColor,
} from '@/constants/project-daily-report';
import { ReportDetailDrawer } from '@/pages/admin/reports/ReportDetailDrawer';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectDailyReportListItem } from '@/types/project-daily-report';

interface ProjectLinkedDailyReportsSectionProps {
  projectId: string;
  projectName?: string;
  showHint?: boolean;
}

function excerpt(text: string | undefined, max = 48): string {
  const value = (text || '').trim();
  if (!value) {
    return '—';
  }
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export function ProjectLinkedDailyReportsSection({
  projectId,
  projectName,
  showHint = true,
}: ProjectLinkedDailyReportsSectionProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState<Dayjs>(() => dayjs());
  const [items, setItems] = useState<ProjectDailyReportListItem[]>([]);
  const [stats, setStats] = useState({ total: 0, submitted: 0, notSubmitted: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReportId, setDetailReportId] = useState<string | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectsApi.fetchProjectDailyReports(projectId, {
        date: date.format('YYYY-MM-DD'),
      });
      setItems(result.items);
      setStats(result.stats);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目日报失败';
      message.error(text);
      setItems([]);
      setStats({ total: 0, submitted: 0, notSubmitted: 0 });
    } finally {
      setLoading(false);
    }
  }, [date, message, projectId]);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const columns: ColumnsType<ProjectDailyReportListItem> = [
    {
      title: '成员',
      dataIndex: ['user', 'name'],
      width: 120,
    },
    {
      title: '手机号',
      dataIndex: ['user', 'phone'],
      width: 130,
    },
    {
      title: '状态',
      dataIndex: 'displayStatus',
      width: 100,
      render: (status: ProjectDailyReportListItem['displayStatus'], record) => (
        <Tag color={projectDailyReportStatusTagColor(status)}>
          {record.displayStatusLabel || PROJECT_DAILY_REPORT_STATUS_LABELS[status]}
        </Tag>
      ),
    },
    {
      title: '今日工作摘要',
      key: 'todayWork',
      render: (_, record) =>
        record.displayStatus === 'NOT_SUBMITTED'
          ? '该日期尚未关联本项目的日报'
          : excerpt(record.report?.todayWork),
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      render: (_, record) =>
        record.navigable && record.reportId ? (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setDetailReportId(record.reportId);
              setDetailOpen(true);
            }}
          >
            查看
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <>
      {showHint ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          项目日报来自成员在「写日报」时关联{projectName ? `「${projectName}」` : '本项目'}
          的个人日报，此处仅展示，不支持后台新建。
        </Typography.Paragraph>
      ) : null}

      <Space style={{ marginBottom: 16 }} wrap>
        <DatePicker
          value={date}
          allowClear={false}
          onChange={(value) => {
            if (value) {
              setDate(value);
            }
          }}
        />
        <Button onClick={() => void loadReports()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8} sm={6}>
          <Statistic title="成员总数" value={stats.total} />
        </Col>
        <Col xs={8} sm={6}>
          <Statistic title="已关联提交" value={stats.submitted} />
        </Col>
        <Col xs={8} sm={6}>
          <Statistic title="未提交/未关联" value={stats.notSubmitted} />
        </Col>
      </Row>

      <Table
        rowKey={(record) => record.user.id}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={false}
        locale={{ emptyText: '暂无成员数据' }}
      />

      <ReportDetailDrawer
        open={detailOpen}
        reportId={detailReportId}
        onClose={() => {
          setDetailOpen(false);
          setDetailReportId(null);
        }}
      />
    </>
  );
}
